import { writable, type Readable } from 'svelte/store';
import type { ResultDetail, RoomResponse } from '../../shared/api';
import { normalizeRoomCode, ROOM_CODE_LENGTH, type GameConfig, type GameVariant } from '../../shared/config';
import { modeConfig, type GameMode } from '../../shared/modes';
import {
  CLOSE_CODES,
  type ClientMessage,
  type Phase,
  type ServerMessage,
  type StateMessage,
} from '../../shared/protocol';
import { randomBytesHex } from '../../shared/random';
import { readDailyStatus } from '../api/daily';
import { QueryHttpError } from '../api/http';
import { RULES_CHECKPOINT } from '../content/tutorial';
import {
  cancelRoom,
  createRoom as createRoomRequest,
  readRoom,
  type PreparedRoomCancellation,
} from '../api/rooms';
import type { BrowserStore } from '../platform/storage';
import { reportDiagnostic } from '../platform/telemetry';
import { createArchiveRetry } from './archive-retry';
import {
  applyIncrementalMessage,
  applyStateMessage,
  recordAcceptedWord,
  resetForLeave,
} from './message-reducer';
import { createProfileSync, type IdentityPatch } from './profile-sync';
import { RoomConnection } from './room-connection';
import { initialSessionState, sessionView, type RoomSessionView } from './room-session-state';
import { roundStartDelay, roundStartLateness, startRoundLocally } from './round-start';
import { Submissions, type Submission } from './submissions';
import { shouldResetInput } from './input-reset';
import { boardActivityMessage } from './endless-board';

export type { RoomSessionView } from './room-session-state';

/** Close code the server uses when another tab takes over this player's seat. */
const CANCEL_TOKEN_BYTES = 32;

/** Map transport failures to the copy the join form shows. */
function roomLookupMessage(error: unknown): string {
  if (error instanceof QueryHttpError && error.status === 404) return 'Room not found!';
  return 'Room service unavailable. Try again.';
}

export interface MessageContext {
  phase: StateMessage['phase'];
  resetInput: boolean;
  submissionFeedback?: 'accept' | 'reject';
}

export interface SessionHooks {
  explainRules(variant: GameVariant): Promise<boolean>;
  message(message: ServerMessage, previous: MessageContext): void;
  disconnected(takenOver: boolean): void;
  joinRequired(): void;
  /** This browser committed to a room; the host owns URL and last-room bookkeeping. */
  roomEntered?(code: string): void;
  archived?(result: ResultDetail): void;
  /** The countdown reached `startAt` and this browser opened play without waiting for a snapshot. */
  roundStarted?(): void;
}

/** One instance per mounted app; screens never own transport or recovered room state. */
export function createRoomSession(store: BrowserStore, hooks: SessionHooks) {
  const state = initialSessionState();
  const { snapshot, form, workflow, identity: credentials } = state;
  const view = sessionView(snapshot, form, workflow, credentials);
  const observable = writable<RoomSessionView>(view);
  let setupIdentity = 0;
  let cancellation: PreparedRoomCancellation | null = null;
  // Stalled words ask the server for one fresh snapshot, which settles or resends them;
  // several stalling together share a single request.
  let resyncRequested = false;
  let roundStartTimer: ReturnType<typeof setTimeout> | undefined;
  /** The phase the server last sent; the snapshot's phase can already have opened play locally. */
  let serverPhase: Phase = 'lobby';
  const submissions = new Submissions(
    () => publish(),
    () => {
      if (resyncRequested || !workflow.ready || !workflow.connected) return;
      resyncRequested = true;
      sendJoin();
    },
  );
  const publish = () => {
    workflow.submissions = [...submissions.entries];
    observable.set(view);
  };
  const profile = createProfileSync(store, credentials, { changed: () => publish() });
  const setProfileCredential = profile.setCredential;
  const ensureProfile = profile.ensure;
  const rejectProfileCredential = profile.reject;
  const connection = new RoomConnection({
    open() {
      workflow.connected = true;
      workflow.ready = false;
      workflow.joinStatus = workflow.joined ? 'joining…' : '';
      if (workflow.joined) sendJoin();
      publish();
    },
    close(code) {
      submissions.disconnected();
      workflow.connected = false;
      workflow.ready = false;
      const takenOver = code === CLOSE_CODES.sessionReplaced;
      if (takenOver) {
        workflow.reconnectStopped = true;
        workflow.joined = false;
        workflow.joinStatus = 'playing in another tab. Join here to take over.';
      } else if (workflow.joined) workflow.joinStatus = 'reconnecting…';
      publish();
      hooks.disconnected(takenOver);
    },
    message: handleMessage,
    clock(offset) {
      workflow.serverOffset = offset;
      const started = scheduleRoundStart();
      publish();
      if (started) hooks.roundStarted?.();
    },
  });
  const archive = createArchiveRetry({
    isCurrent: (context) =>
      context.identity === setupIdentity && context.credential === credentials.profileCredential,
    pending(roundId) {
      workflow.archivePendingRoundId = roundId;
    },
    success(result) {
      submissions.clear();
      connection.stop();
      Object.assign(workflow, {
        archivePendingRoundId: null,
        connected: false,
        joined: false,
        ready: false,
        inSetupLobby: false,
        hasSnapshot: true,
        joinStatus: '',
      });
      snapshot.archivedResult = result;
      snapshot.phase = 'results';
      publish();
      hooks.archived?.(result);
    },
    terminal(status, credential) {
      if (status === 401 && credential) rejectProfileCredential(credential);
      workflow.archivePendingRoundId = null;
      workflow.joinStatus =
        status === 401
          ? 'Profile expired. Join again.'
          : status === 404
            ? 'This result belongs to another profile'
            : 'Result unavailable. Try again.';
      publish();
    },
    retrying() {
      workflow.joinStatus = 'Result unavailable. Retrying…';
      publish();
    },
  });
  function loadArchive(roundId: string, identity = setupIdentity): Promise<void> {
    return archive.load(roundId, { identity, credential: credentials.profileCredential });
  }

  /** Commit this browser to a room code: form, storage and the host's URL. */
  function enterRoom(code: string): void {
    workflow.roomCode = code;
    form.roomInput = code;
    hooks.roomEntered?.(code);
  }

  async function prepareSetup(): Promise<void> {
    leave();
    const identity = setupIdentity;
    const mode = form.selectedMode;
    const config = modeConfig(mode);
    const token = randomBytesHex(CANCEL_TOKEN_BYTES);
    workflow.inSetupLobby = true;
    workflow.creatingRoom = true;
    workflow.joinStatus = 'getting ready…';
    publish();
    try {
      if (!credentials.profileCredential || !credentials.profileSynced) await ensureProfile();
      if (identity !== setupIdentity) return;
      const credential = credentials.profileCredential!;
      if (mode === 'daily') {
        let status;
        try {
          status = await readDailyStatus(credential);
        } catch (error) {
          if (identity !== setupIdentity || credential !== credentials.profileCredential) return;
          if (error instanceof QueryHttpError && error.status === 401) rejectProfileCredential(credential);
          throw new Error('Could not check Daily. Try again.');
        }
        if (identity !== setupIdentity || credential !== credentials.profileCredential) return;
        if (status.completed && status.roundId) {
          await loadArchive(status.roundId, identity);
          return;
        }
      }
      if (identity !== setupIdentity) return;
      let room: RoomResponse;
      try {
        room = await createRoomRequest({
          mode,
          config,
          cancelToken: token,
          credential,
        });
      } catch (error) {
        const current =
          identity === setupIdentity && (mode !== 'daily' || credential === credentials.profileCredential);
        if (
          current &&
          mode === 'daily' &&
          credential &&
          error instanceof QueryHttpError &&
          error.status === 401
        )
          rejectProfileCredential(credential);
        throw new Error('Could not prepare a room. Try again.');
      }
      const created = { code: room.code, token };
      if (identity !== setupIdentity || (mode === 'daily' && credential !== credentials.profileCredential)) {
        cancelRoom(created);
        return;
      }
      cancellation = created;
      workflow.roomCode = room.code;
      form.roomInput = room.code;
      await joinRoom(room, identity);
    } catch (error) {
      if (identity === setupIdentity) {
        workflow.autoStart = false;
        workflow.joinStatus = error instanceof Error ? error.message : 'Could not prepare a room. Try again.';
      }
    } finally {
      if (identity === setupIdentity) {
        workflow.creatingRoom = false;
        publish();
      }
    }
  }

  async function playSetup(): Promise<void> {
    if (!workflow.creatingRoom && !workflow.joined) void prepareSetup();
    const identity = setupIdentity;
    if (
      !(await hooks.explainRules(view.config.variant)) ||
      identity !== setupIdentity ||
      !workflow.inSetupLobby
    )
      return;
    if (!workflow.creatingRoom && !workflow.joiningRoom && !workflow.joined) return;
    workflow.autoStart = true;
    startPrepared();
    publish();
  }

  function startPrepared(): void {
    if (workflow.inSetupLobby && workflow.autoStart && workflow.ready && snapshot.phase === 'lobby') {
      if (send({ t: 'start' })) workflow.autoStart = false;
    }
  }

  /** The Host form's operation: always a Versus room from `hostConfig`, whatever mode came before. */
  async function createRoom(): Promise<void> {
    if (workflow.creatingRoom || workflow.joiningRoom) return;
    const identity = setupIdentity;
    form.selectedMode = 'versus';
    workflow.autoStart = false;
    workflow.creatingRoom = true;
    publish();
    if (!(await hooks.explainRules(view.config.variant))) {
      if (identity === setupIdentity) {
        workflow.creatingRoom = false;
        publish();
      }
      return;
    }
    if (identity !== setupIdentity) return;
    workflow.joinStatus = 'creating a room…';
    try {
      await ensureProfile();
      if (identity !== setupIdentity) return;
      const value = await createRoomRequest({
        mode: 'versus',
        config: form.hostConfig,
        credential: credentials.profileCredential,
      });
      if (identity !== setupIdentity) return;
      enterRoom(normalizeRoomCode(value.code));
      await joinRoom(value);
    } catch {
      if (identity !== setupIdentity) return;
      workflow.autoStart = false;
      workflow.joinStatus = 'Could not create a room. Try again.';
    } finally {
      if (identity === setupIdentity) {
        workflow.creatingRoom = false;
        publish();
      }
    }
  }

  async function joinRoom(created?: RoomResponse, identity = setupIdentity): Promise<void> {
    if (workflow.joiningRoom) return;
    archive.cancel();
    workflow.archivePendingRoundId = null;
    workflow.joiningRoom = true;
    workflow.hasSnapshot = false;
    publish();
    try {
      const code = normalizeRoomCode(form.roomInput || workflow.roomCode);
      if (code.length !== ROOM_CODE_LENGTH) {
        workflow.joinStatus = 'enter a six-letter room code';
        return;
      }
      let info: RoomResponse;
      try {
        info = created ?? (await readRoom(code));
      } catch (error) {
        throw new Error(roomLookupMessage(error));
      }
      if (identity !== setupIdentity) return;
      if (info.archiveRoundId) {
        await ensureProfile();
        if (identity !== setupIdentity) return;
        await loadArchive(info.archiveRoundId, identity);
        return;
      }
      workflow.hasRoomInfo = true;
      snapshot.mode = info.mode;
      snapshot.config = info.config;
      snapshot.dailyDate = info.dailyDate ?? null;
      form.selectedMode = info.mode;
      // Only room-checkpoint modes teach at join; the others taught before the room existed.
      if (RULES_CHECKPOINT[info.mode] === 'room' && !(await hooks.explainRules(view.config.variant))) {
        workflow.autoStart = false;
        return;
      }
      if (identity !== setupIdentity) return;
      workflow.roomCode = code;
      form.roomInput = code;
      credentials.roomToken = store.roomToken(code);
      await ensureProfile();
      if (identity !== setupIdentity) return;
      workflow.joined = true;
      workflow.reconnectStopped = false;
      workflow.ready = false;
      workflow.joinStatus = workflow.connected ? 'joining…' : 'connecting…';
      enterRoom(code);
      connect();
    } catch (error) {
      if (identity !== setupIdentity) return;
      workflow.autoStart = false;
      workflow.joinStatus = error instanceof Error ? error.message : 'Could not join. Try again.';
      workflow.joined = false;
    } finally {
      if (identity === setupIdentity) {
        workflow.joiningRoom = false;
        publish();
      }
    }
  }

  function send(message: ClientMessage): boolean {
    return connection.send(message);
  }

  function sendJoin(): void {
    send({
      t: 'join',
      ...(credentials.roomToken ? { token: credentials.roomToken } : {}),
      ...(credentials.profileCredential ? { profileCredential: credentials.profileCredential } : {}),
    });
  }

  function connect(): void {
    if (!workflow.roomCode || workflow.reconnectStopped || !workflow.joined) return;
    if (connection.isOpen(workflow.roomCode)) {
      if (!workflow.ready) sendJoin();
    } else {
      workflow.connected = false;
      workflow.ready = false;
      connection.connect(workflow.roomCode);
    }
  }

  function applyState(message: StateMessage): void {
    resyncRequested = false;
    const applied = applyStateMessage(state, message, Date.now());
    flushSubmissions(submissions.reconcile(message.currentRoundId, applied.found));
    if (applied.cancelArchive) {
      archive.cancel();
      workflow.archivePendingRoundId = null;
    }
    const own = applied.ownResult;
    if (!own) return;
    const key = `${workflow.roomCode}:${own.roundId}:${snapshot.meId}`;
    if (key !== workflow.recordedResult && snapshot.results) {
      store.addResults(workflow.roomCode, snapshot.results, snapshot.meId);
      workflow.recordedResult = key;
    }
  }

  /**
   * Open play at `startAt` for the countdown in the snapshot. Returns true when it was already
   * due and opened now; the caller publishes. A timer that fires later publishes and cues itself.
   */
  function scheduleRoundStart(): boolean {
    clearTimeout(roundStartTimer);
    const delay = roundStartDelay(snapshot, Date.now() + workflow.serverOffset);
    if (delay === null) return false;
    if (delay === 0) return startRoundLocally(snapshot);
    roundStartTimer = setTimeout(() => {
      if (!startRoundLocally(snapshot)) return;
      publish();
      hooks.roundStarted?.();
    }, delay);
    return false;
  }

  function flushSubmissions(entries: readonly Submission[]): void {
    if (snapshot.phase !== 'playing' || !snapshot.roundId) return;
    for (const entry of entries) {
      if (send(wordMessage(entry))) submissions.sent(entry);
    }
  }

  function wordMessage(entry: Submission): ClientMessage {
    return {
      t: 'word',
      w: entry.word,
      roundId: entry.round,
      submissionId: entry.id,
      ...(entry.path ? { path: entry.path } : {}),
      ...(entry.pickupIds ? { pickupIds: entry.pickupIds } : {}),
    };
  }

  function stopJoined(joinStatus: string, reconnectStopped = workflow.reconnectStopped): void {
    connection.stop();
    workflow.connected = false;
    workflow.joined = false;
    workflow.ready = false;
    workflow.reconnectStopped = reconnectStopped;
    workflow.joinStatus = joinStatus;
  }

  function handleMessage(message: ServerMessage): void {
    const previous: MessageContext = {
      phase: snapshot.phase,
      resetInput: shouldResetInput(snapshot, message),
    };
    if (!applyIncrementalMessage(snapshot, message)) {
      switch (message.t) {
        case 'blocked':
          profile.setBlock(message.blockedUntil, message.blockMessage, message.blockedFeatures);
          break;
        case 'welcome':
          snapshot.meId = message.id;
          if (message.token) {
            credentials.roomToken = message.token;
            store.saveRoomToken(workflow.roomCode, message.token);
          }
          break;
        case 'state': {
          const late = roundStartLateness(
            { serverPhase, roundId: snapshot.roundId },
            message,
            Date.now() + workflow.serverOffset,
          );
          if (late !== null) reportDiagnostic({ kind: 'round_start_late', durationMs: late });
          serverPhase = message.phase;
          applyState(message);
          // A countdown already due opens here, before publishing, so the overlay never flashes
          // back; `App` cues the start from the published transition.
          scheduleRoundStart();
          if (snapshot.phase !== 'lobby') cancellation = null;
          startPrepared();
          break;
        }
        case 'wordResult': {
          const settled = submissions.result(message);
          if (settled) {
            // A presumed word already played its accepted cue; only a reversal is news.
            if (!message.ok) previous.submissionFeedback = 'reject';
            else if (settled.presumedPoints === undefined) previous.submissionFeedback = 'accept';
            // A word on the old socket can commit after a reconnect snapshot.
            // A duplicate proves it exists, but only a snapshot supplies its points.
            if (
              !message.ok &&
              message.reason === 'dup' &&
              message.roundId === snapshot.roundId &&
              !snapshot.found.has(message.w)
            )
              sendJoin();
          }
          recordAcceptedWord(snapshot, message);
          break;
        }
        case 'dailyAttempt':
          leave();
          workflow.roomCode = message.roomCode;
          form.roomInput = message.roomCode;
          void joinRoom();
          break;
        case 'error':
          if (submissions.entries.some((entry) => entry.pending))
            reportDiagnostic({ kind: 'submission_error' });
          workflow.autoStart = false;
          workflow.joinStatus = message.msg;
          if (message.code === 'profile') {
            setProfileCredential(null);
            forgetRoomToken();
          }
          if (
            ['full', 'auth', 'profile'].includes(message.code) ||
            (message.code === 'invalid' && !workflow.ready)
          ) {
            stopJoined(message.msg);
            if (!workflow.inSetupLobby) hooks.joinRequired();
          }
          break;
        case 'kicked':
          stopJoined(message.reason === 'left' ? '' : `you were ${message.reason}`, true);
          forgetRoomToken();
          hooks.joinRequired();
          break;
      }
    }
    publish();
    hooks.message(message, previous);
  }

  /** Invalidate every in-flight room workflow and stop the transport. */
  function stopRoomWork(): void {
    submissions.clear();
    setupIdentity++;
    archive.cancel();
    connection.stop();
    clearTimeout(roundStartTimer);
  }

  function forgetRoomToken(): void {
    credentials.roomToken = null;
    store.saveRoomToken(workflow.roomCode, null);
  }

  function leave(): void {
    if (cancellation && snapshot.phase === 'lobby') cancelRoom(cancellation);
    cancellation = null;
    send({ t: 'leave' });
    stopRoomWork();
    forgetRoomToken();
    resetForLeave(state);
    publish();
  }

  /** Stop the transport but keep membership and the reconnect token: turn-taking rooms are revisited. */
  function detach(): void {
    cancellation = null;
    stopRoomWork();
    resetForLeave(state);
    publish();
  }

  function selectForm(mode: GameMode): void {
    form.selectedMode = mode;
    if (mode === 'versus') form.hostConfig = modeConfig(mode);
    workflow.hasRoomInfo = workflow.hasSnapshot || workflow.joined;
    workflow.joinStatus = '';
    publish();
  }

  return {
    subscribe: observable.subscribe as Readable<RoomSessionView>['subscribe'],

    hydrate(pathCode?: string): void {
      profile.hydrate();
      workflow.roomCode = pathCode ? normalizeRoomCode(pathCode) : (store.roomCode ?? '');
      form.roomInput = workflow.roomCode;
      publish();
    },
    /** Refresh identity and feature eligibility; safe to call on every resume/poll. */
    async refreshProfile(): Promise<void> {
      const hadBlock = credentials.blockedUntil !== null;
      await profile.load();
      if (
        hadBlock &&
        !(credentials.blockedUntil !== null && credentials.blockedUntil > Date.now()) &&
        workflow.joined &&
        connection.isOpen(workflow.roomCode)
      )
        sendJoin();
    },
    loadProfile: profile.load,
    profileBlockExpired(): void {
      if (credentials.blockedUntil === null || credentials.blockedUntil > Date.now()) return;
      profile.setBlock(null);
      if (workflow.joined && connection.isOpen(workflow.roomCode)) sendJoin();
    },
    /** Onboarding and Settings edits; written through to the profile in the background. */
    setIdentity(patch: IdentityPatch): void {
      profile.setIdentity(patch);
    },
    setRoomInput(value: string): void {
      form.roomInput = value;
      publish();
    },
    selectSetupMode: selectForm,
    selectVersusForm: () => selectForm('versus'),
    updateHostConfig(config: GameConfig): void {
      form.hostConfig = { ...config };
      publish();
    },
    clearJoinStatus(): void {
      workflow.joinStatus = '';
      publish();
    },
    cancelAutoStart(): void {
      workflow.autoStart = false;
      publish();
    },
    dismissResults(): void {
      workflow.dismissedResultsRoundId = snapshot.results?.roundId ?? null;
      publish();
    },
    /** Show the last completed round again from the turn lobby. */
    recallResults(): void {
      workflow.dismissedResultsRoundId = null;
      publish();
    },
    resumeInvitation(): Promise<void> {
      workflow.joinStatus = 'reconnecting…';
      publish();
      return joinRoom();
    },
    createRoom,
    rejectProfileCredential,
    getProfileCredential: async (): Promise<string> => {
      await ensureProfile();
      publish();
      if (!credentials.profileCredential) throw new Error('Profile credential unavailable');
      return credentials.profileCredential;
    },
    prepareSetup,
    playSetup,
    joinRoom: (created?: RoomResponse) => joinRoom(created),
    startRound: () =>
      send({
        t: 'start',
        ...(snapshot.phase === 'results' && snapshot.results
          ? { afterRoundId: snapshot.results.roundId }
          : {}),
      }),
    confirmReady: () => send({ t: 'ready' }),
    cancelStart: () => send({ t: 'cancelStart' }),
    resetScores: () => send({ t: 'resetScores' }),
    kickPlayer: (id: string) => send({ t: 'kick', id }),
    sendChat: (text: string) => send({ t: 'chat', text }),
    finishRound: () => (snapshot.roundId ? send({ t: 'finish', roundId: snapshot.roundId }) : false),
    beginTurn: () => (snapshot.roundId ? send({ t: 'begin', roundId: snapshot.roundId }) : false),
    requestHint: () => (snapshot.roundId ? send({ t: 'hint', roundId: snapshot.roundId }) : false),
    /** Endless: the player touched the board; the first touch on each board starts its clock. */
    markBoardActive(): boolean {
      const message = boardActivityMessage(snapshot);
      return message ? send(message) : false;
    },
    updateRoomConfig: (config: Partial<GameConfig>) => send({ t: 'setConfig', config }),
    /**
     * `queued` words wait for the recovery snapshot; the server still judges their timing.
     * `presumedPoints` marks a solution-filter hit the screen has already presented as accepted.
     */
    submitWord(
      word: string,
      path?: readonly number[],
      pickupIds?: readonly string[],
      presumedPoints?: number,
    ): 'sent' | 'queued' | 'blocked' {
      if (snapshot.finished || snapshot.phase !== 'playing' || !snapshot.roundId) return 'blocked';
      const entry = submissions.add(snapshot.roundId, word, path, pickupIds, presumedPoints);
      if (!entry) return 'blocked';
      const live = workflow.ready && workflow.connected;
      const sent = live && send(wordMessage(entry));
      if (sent) submissions.sent(entry);
      publish();
      return sent ? 'sent' : 'queued';
    },
    connect,
    leave,
    detach,
    stop: stopRoomWork,
    isOpen: () => connection.isOpen(workflow.roomCode),
    syncClock: () => connection.syncClock(),
  };
}

export type RoomSession = ReturnType<typeof createRoomSession>;
